import { MouseEvent, ReactNode } from 'react';
import { Button, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

// Shared mobile/compact button styles and icon sizes so other
// controls (like the export button) can exactly match the
// NewActionButton appearance in all uses
export const getCompactButtonSx = (isMobile: boolean) => ({
  whiteSpace: 'nowrap',
  minWidth: isMobile ? 0 : 'fit-content',
  p: isMobile ? 0.5 : 1,
  maxHeight: 36,
});

export const compactIconFontSize: 'small' | 'inherit' | 'medium' | 'large' = 'small';
export const compactSpinnerSize = 20;

type NewActionButtonProps = {
  id?: string;
  ariaLabel?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  open?: boolean;
  disabled?: boolean;
  tooltip?: ReactNode;
  startIcon?: ReactNode;
  showArrow?: boolean;
  children?: ReactNode;
};

export const NewActionButton = ({
  id,
  ariaLabel,
  onClick,
  open,
  disabled,
  tooltip,
  startIcon,
  showArrow = true,
  children,
}: NewActionButtonProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const btnProps: any = {};
  if (id) {
    btnProps.id = id;
    btnProps['data-testid'] = id;
  }

  return (
    <Tooltip
      title={tooltip}
      placement="bottom"
    >
      <span>
        <Button
          {...btnProps}
          aria-label={ariaLabel}
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          sx={{
            whiteSpace: 'nowrap',
            minWidth: isMobile ? 0 : 'fit-content',
            p: isMobile ? 0.5 : 1,
          }}
          onClick={onClick}
          startIcon={isMobile ? undefined : startIcon}
          endIcon={
            showArrow && !isMobile ? (
              open ? (
                <ArrowDropUpIcon />
              ) : (
                <ArrowDropDownIcon />
              )
            ) : undefined
          }
          disableElevation
          disabled={disabled}
        >
          {isMobile ? <AddIcon fontSize="small" /> : children}
        </Button>
      </span>
    </Tooltip>
  );
};

NewActionButton.displayName = 'NewActionButton';
export default NewActionButton;
